import { Form, Formik } from "formik";
import { useField } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, dollarsToCents, formatCents } from "@/lib/utils";
import type { FlowPatch } from "@/features/oic/api";
import { offerForPlan, type OICFlow, type OICSummary, type OfferPlan } from "@/features/oic/types";

interface Props {
  flow: OICFlow;
  summary: OICSummary | null;
  onSave: (patch: FlowPatch) => Promise<OICFlow>;
  onContinue: () => void;
}

interface Values {
  offerPlan: OfferPlan;
  offerDollars: string;
  basis: "" | "DATC" | "ETA_HARDSHIP" | "ETA_PUBLIC_POLICY";
}

const schema = Yup.object({
  offerPlan: Yup.number().oneOf([12, 24]).required("Choose a payment plan."),
  offerDollars: Yup.number()
    .typeError("Enter a whole-dollar amount.")
    .integer("Whole dollars only.")
    .min(1, "The offer must be at least $1.")
    .required("Enter the offer amount."),
  basis: Yup.string()
    .oneOf(["DATC", "ETA_HARDSHIP", "ETA_PUBLIC_POLICY"], "Choose a basis.")
    .required("Choose a basis."),
});

const BASIS_OPTIONS = [
  { value: "DATC", label: "Doubt as to Collectibility" },
  { value: "ETA_HARDSHIP", label: "Effective Tax Administration — Economic Hardship" },
  { value: "ETA_PUBLIC_POLICY", label: "Effective Tax Administration — Public Policy" },
] as const;

/**
 * Step 4 — Terms. Payment plan (lump-sum 12 / periodic 24), the actual offer
 * amount (whole dollars ≥ $1, "use calculated" fills from the selected plan's
 * computed amount), and the basis (drives the 656 reason prefill). Client-side
 * validation is UX only — the server re-validates (`offer_amount ≥ 1` whole
 * dollars, plan ∈ {12,24}, basis enum).
 */
export function TermsStep({ flow, summary, onSave, onContinue }: Props) {
  const initial: Values = {
    offerPlan: (flow.offerPlan ?? 12) as OfferPlan,
    offerDollars: flow.offerAmountCents != null ? String(Math.round(flow.offerAmountCents / 100)) : "",
    basis: flow.basis || "",
  };

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      onSubmit={async (values, helpers) => {
        try {
          await onSave({
            offerPlan: values.offerPlan,
            offerAmountCents: dollarsToCents(values.offerDollars),
            basis: values.basis,
          });
          toast.success("Terms saved.");
          onContinue();
        } catch {
          toast.error("Couldn't save the terms.");
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        const calculated = summary?.available ? offerForPlan(summary, values.offerPlan) : undefined;
        const enteredCents = values.offerDollars ? dollarsToCents(values.offerDollars) : null;
        const matches = calculated != null && enteredCents === calculated;

        return (
          <Form className="grid max-w-xl gap-6">
            <div className="grid gap-2">
              <Label>Payment plan</Label>
              <RadioGroup
                value={String(values.offerPlan)}
                onValueChange={(v) => setFieldValue("offerPlan", Number(v) as OfferPlan)}
                className="grid gap-2"
              >
                <PlanOption
                  value={12}
                  label="Lump-Sum Cash"
                  hint="Settle over 12 future months"
                  amount={summary?.available ? offerForPlan(summary, 12) : undefined}
                  selected={values.offerPlan === 12}
                />
                <PlanOption
                  value={24}
                  label="Periodic Payment"
                  hint="Settle over 24 future months"
                  amount={summary?.available ? offerForPlan(summary, 24) : undefined}
                  selected={values.offerPlan === 24}
                />
              </RadioGroup>
            </div>

            <div className="grid gap-2">
              <FormField
                name="offerDollars"
                label="Actual offer amount ($)"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={calculated == null}
                  onClick={() =>
                    calculated != null &&
                    setFieldValue("offerDollars", String(Math.round(calculated / 100)))
                  }
                >
                  Use calculated ({calculated != null ? formatCents(calculated) : "—"})
                </Button>
                {values.offerDollars && calculated != null && (
                  <span className={cn("text-sm", matches ? "text-success" : "text-warning")}>
                    {matches ? "Matches calculated" : "Differs from calculated"}
                  </span>
                )}
              </div>
            </div>

            <BasisSelect />

            <div>
              <Button type="submit" disabled={isSubmitting}>
                Save terms &amp; continue
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

function PlanOption({
  value,
  label,
  hint,
  amount,
  selected,
}: {
  value: OfferPlan;
  label: string;
  hint: string;
  amount?: number;
  selected: boolean;
}) {
  return (
    <Label
      htmlFor={`plan-${value}`}
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-md border p-3",
        selected && "border-primary bg-primary/5",
      )}
    >
      <RadioGroupItem id={`plan-${value}`} value={String(value)} />
      <span className="flex-1">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground block text-xs">{hint}</span>
      </span>
      {amount != null && <span className="tabular-nums font-semibold">{formatCents(amount)}</span>}
    </Label>
  );
}

function BasisSelect() {
  const [field, meta, helpers] = useField<Values["basis"]>("basis");
  const hasError = meta.touched && !!meta.error;
  return (
    <div className="grid gap-2">
      <Label htmlFor="basis">Basis for the offer</Label>
      <Select value={field.value || undefined} onValueChange={(v) => helpers.setValue(v as Values["basis"])}>
        <SelectTrigger id="basis" aria-invalid={hasError}>
          <SelectValue placeholder="Select a basis…" />
        </SelectTrigger>
        <SelectContent>
          {BASIS_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasError && <p className="text-destructive text-sm">{meta.error}</p>}
    </div>
  );
}
